/**
 * What an auto-fill rule does, in a sentence.
 *
 * A rule is authored in four or five dropdowns, and a firm with ten of them cannot tell one card
 * from another: they are identical until you read every select inside. That is no way to find the
 * one you came to change. This renders the same information as a line of prose above the
 * controls.
 *
 * Pure, and here rather than inline in the admin page, because a summary that misdescribes a rule
 * is worse than none at all — someone would switch off the wrong one.
 */

/**
 * The field list labels every field "Section — Field", which is right for a dropdown of two
 * hundred and wrong in a sentence, where the section is repeated on every line and the field name
 * is the part being looked for.
 */
export function shortFieldLabel(field, path) {
  if (field && typeof field.label === 'string' && field.label.trim()) {
    return field.label.split(' — ').pop().trim();
  }
  // A rule pointing at a deleted field still has to describe itself; the path is what there is.
  return String(path || '').trim() || 'a field not chosen yet';
}

/**
 * @param {object} rule
 * @param {(path: string) => object|undefined} fieldFor  looks a field up by path
 * @param {Array<[string, string]>} attributes  the [id, label] pairs offered for this rule's
 *   directory, so the sentence names the attribute the way the picker does
 */
export function derivationRuleSummary(rule, fieldFor, attributes = []) {
  if (!rule || typeof rule !== 'object') return '';
  const at = (path) => (typeof fieldFor === 'function' ? fieldFor(path) : undefined);
  const target = shortFieldLabel(at(rule.targetPath), rule.targetPath);
  const source = shortFieldLabel(at(rule.sourcePath), rule.sourcePath);

  if (rule.kind === 'lookup') {
    const found = (attributes || []).find(([id]) => id === rule.attribute);
    const attribute = (found && found[1]) || rule.attribute || 'a value';
    return `${source} → its ${attribute} fills ${target}`;
  }
  if (rule.kind === 'mirror') return `${source} → ${target}, kept in step`;
  if (rule.kind === 'appendPeople') return `${source} → added to ${target}`;
  if (rule.kind === 'setDefault') return `Sets ${target} when its condition first matches`;
  // An unknown kind is a rule the form will skip. Saying so is more use than an empty line.
  return `${target} — this rule's type is not one the form knows, so it does nothing`;
}
